import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2, Settings, Clock, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { apiErrorMessage, serverDetail } from '@/utils/apiError';
import { formatDateTimeWithZone } from '@/utils/formatDateTime';
import { apiClient, TenantSettingsUpdateRequest } from '../services/api';
import type { DealMapping } from '../types/api';
import { isUnpaidInvoice } from '@/utils/invoiceStatus';
import {
  buildAllFilterOptions,
  hasActiveColumnFilters,
  matchesColumnFilters,
  nextSort,
  sortDeals,
  NO_COLUMN_FILTERS,
  type ColumnFilterKey,
  type ColumnFilters,
  type DealSort,
  type SortKey,
} from '@/utils/dealTableFilters';
import DealListItem from './DealListItem';
import DealsTable from './DealsTable';
import SyncFailureAlert from './SyncFailureAlert';
import LinkExistingDialog from './LinkExistingDialog';
import BulkLinkButton from './BulkLinkButton';
import SyncConfirmationDialog from './SyncConfirmationDialog';

type ViewMode = 'list' | 'table';

const CRON_OPTIONS = [
  { label: 'Every 6 hours', value: '0 */6 * * *' },
  { label: 'Every 12 hours', value: '0 */12 * * *' },
  { label: 'Daily (midnight)', value: '0 0 * * *' },
  { label: 'Weekly (Monday)', value: '0 0 * * 1' },
];

// Deal details open in a new tab so the list keeps its search, filters,
// sort and scroll position. The Descope session is shared across tabs.
const openDealInNewTab = (deal: DealMapping) => {
  window.open(
    `/sync_deals/${encodeURIComponent(deal.hubspot_deal_id)}`,
    '_blank',
    'noopener,noreferrer',
  );
};

const DealList: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  // The table is the primary view: it fits company, close date and invoice
  // status on one row, which the cards can't. The card list stays available.
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [onlyWarnings, setOnlyWarnings] = useState(false);
  const [onlyUnlinked, setOnlyUnlinked] = useState(false);
  const [onlyUnpaid, setOnlyUnpaid] = useState(false);
  // Column sort and per-column filters live here rather than inside the table
  // so the list view stays in step with it, and so the "no deals match" empty
  // state — which the table can't render, having no rows to render it in —
  // knows why the set came back empty.
  const [sort, setSort] = useState<DealSort | null>(null);
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>(NO_COLUMN_FILTERS);
  // Holds the deal whose "Link Existing…" dialog is open (dialog added in the
  // next step). null = no dialog open.
  const [linkTarget, setLinkTarget] = useState<DealMapping | null>(null);
  const [syncTarget, setSyncTarget] = useState<DealMapping | null>(null);
  const [syncingDealId, setSyncingDealId] = useState<string | null>(null);

  const { data: tenantSettings } = useQuery({
    queryKey: ['tenantSettings'],
    queryFn: () => apiClient.getTenantSettings(),
    retry: 1,
  });

  const updateSettings = useMutation({
    mutationFn: (update: TenantSettingsUpdateRequest) =>
      apiClient.updateTenantSettings(update),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenantSettings'] });
    },
  });

  // Sync action for the table view (the card list has its own per-row sync).
  const syncMutation = useMutation({
    mutationFn: (dealId: string) => apiClient.syncDeal(dealId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['deals'] }),
  });

  const handleTableSync = (deal: DealMapping) => {
    setSyncTarget(deal);
  };

  const confirmTableSync = () => {
    if (!syncTarget) return;
    // Closing the dialog here is correct, but it means syncTarget can no longer
    // tell the table which row is in flight — track the id separately so the
    // row's Sync button can show progress instead of appearing to do nothing.
    setSyncingDealId(syncTarget.hubspot_deal_id);
    syncMutation.mutate(syncTarget.hubspot_deal_id, {
      onSettled: () => setSyncingDealId(null),
    });
    setSyncTarget(null);
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['deals'],
    queryFn: () => apiClient.listDeals(),
    refetchInterval: (query) => query.state.error ? false : 5000,
    retry: (failureCount, err: any) => {
      // Don't retry on 400 (credentials not configured)
      if (err?.response?.status === 400) return false;
      return failureCount < 3;
    },
  });

  // Search and the chip filters, applied first. The column selects offer the
  // values found in THIS set rather than the fully filtered one: options built
  // from the final set would collapse to the single value just chosen, leaving
  // no way to pick another.
  const searchedDeals = useMemo(() => {
    const searchLower = searchQuery.toLowerCase();
    return (data?.deals ?? []).filter((deal) => {
      const matchesSearch =
        !searchLower ||
        deal.dealname?.toLowerCase().includes(searchLower) ||
        // The table no longer shows a Company column, so search is the only way
        // to reach a deal by its company — it has to match here or the name
        // would be unreachable.
        deal.company_name?.toLowerCase().includes(searchLower) ||
        deal.hubspot_deal_id.toLowerCase().includes(searchLower) ||
        deal.stripe_subscription_id?.toLowerCase().includes(searchLower) ||
        // So "signed" narrows to signed quotes without needing a filter chip.
        deal.quote_status?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
      if (onlyWarnings && !deal.warnings?.length) return false;
      if (onlyUnlinked && deal.sync_status !== 'not_synced') return false;
      if (onlyUnpaid && !isUnpaidInvoice(deal)) return false;
      return true;
    });
  }, [data, searchQuery, onlyWarnings, onlyUnlinked, onlyUnpaid]);

  const filterOptions = useMemo(
    () => buildAllFilterOptions(searchedDeals, columnFilters),
    [searchedDeals, columnFilters],
  );

  const filteredDeals = useMemo(
    () =>
      sortDeals(
        searchedDeals.filter((deal) => matchesColumnFilters(deal, columnFilters)),
        sort,
      ),
    [searchedDeals, columnFilters, sort],
  );

  const columnFiltersActive = hasActiveColumnFilters(columnFilters);

  const handleToggleSort = (key: SortKey) => setSort((current) => nextSort(current, key));

  const handleFilterChange = (key: ColumnFilterKey, value: string) =>
    setColumnFilters((current) => ({ ...current, [key]: value }));

  const clearColumnFilters = () => setColumnFilters(NO_COLUMN_FILTERS);

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        {/* Page Title */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground">Deals</h1>
          <p className="text-muted-foreground">Manage and sync your HubSpot deals to Stripe subscriptions.</p>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
            <Input
              type="text"
              // Names company explicitly: with no Company column, search is
              // how you find a deal by its customer, and that isn't guessable
              // from "Search deals".
              placeholder="Search by deal, company, or ID…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12"
            />
          </div>
        </div>

        {/* Auto-sync Toggle */}
        <div className="mb-6">
          <Card className={cn(tenantSettings?.auto_sync && "border-green-200 bg-green-50")}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="auto-sync" className="text-sm font-medium">Auto-sync</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Automatically sync deals on a schedule
                  </p>
                </div>
                <Switch
                  id="auto-sync"
                  checked={tenantSettings?.auto_sync ?? false}
                  className="data-[state=checked]:bg-green-600"
                  onCheckedChange={(checked) => {
                    updateSettings.mutate({ auto_sync: checked });
                  }}
                />
              </div>

              {tenantSettings?.auto_sync && (
                <div className="space-y-3 pt-3 border-t border-green-200">
                  {tenantSettings.auto_sync_enabled_at && (
                    <div className="flex items-center gap-2 text-sm text-green-700">
                      <Clock className="h-4 w-4 shrink-0" />
                      <span>
                        Only deals updated after{' '}
                        <span className="font-medium">
                          {formatDateTimeWithZone(tenantSettings.auto_sync_enabled_at)}
                        </span>
                        {' '}will be auto-synced
                      </span>
                    </div>
                  )}

                  <div className="flex items-center gap-3">
                    <Label className="text-sm text-green-700">Schedule:</Label>
                    <Select
                      value={tenantSettings.auto_sync_cron ?? '0 0 * * *'}
                      onValueChange={(value) => {
                        updateSettings.mutate({ auto_sync_cron: value });
                      }}
                    >
                      <SelectTrigger className="w-[200px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CRON_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Bulk Link */}
        <div className="mb-6 flex items-center justify-end">
          <BulkLinkButton />
        </div>

        {/* Filters + view toggle */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant={onlyWarnings ? 'default' : 'outline'}
            onClick={() => setOnlyWarnings((v) => !v)}
          >
            Only warnings
          </Button>
          <Button
            size="sm"
            variant={onlyUnlinked ? 'default' : 'outline'}
            onClick={() => setOnlyUnlinked((v) => !v)}
          >
            Not synced
          </Button>
          <Button
            size="sm"
            variant={onlyUnpaid ? 'default' : 'outline'}
            onClick={() => setOnlyUnpaid((v) => !v)}
          >
            Unpaid invoices
          </Button>
          {/* The column filters live in the table's own headers, which the
              card list doesn't render — without this, switching to list view
              while filtered would hide rows with nothing on screen saying so,
              and no way to undo it. */}
          {columnFiltersActive && (
            <Button size="sm" variant="ghost" onClick={clearColumnFilters}>
              <X className="mr-1 h-3.5 w-3.5" />
              Clear column filters
            </Button>
          )}
          <div className="ml-auto">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setViewMode((m) => (m === 'list' ? 'table' : 'list'))}
            >
              {viewMode === 'list' ? 'Table view' : 'List view'}
            </Button>
          </div>
        </div>

        {/* Deals List */}
        <div>
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <p className="mt-4 text-muted-foreground">Loading deals...</p>
            </div>
          )}

          {error && (() => {
            const axiosError = error as any;
            const status = axiosError?.response?.status;
            const message = serverDetail(error);
            const isCredentialsMissing =
              status === 400 &&
              !!message &&
              message.toLowerCase().includes('credentials not configured');

            if (isCredentialsMissing) {
              return (
                <Card className="border-amber-200 bg-amber-50">
                  <CardContent className="flex flex-col items-center justify-center p-8 space-y-4">
                    <Settings className="h-12 w-12 text-amber-600" />
                    <div className="text-center">
                      <div className="font-semibold text-foreground">API Keys Not Configured</div>
                      <div className="text-sm text-muted-foreground mt-1">
                        Set up your HubSpot and Stripe API keys to start syncing deals.
                      </div>
                    </div>
                    <Button onClick={() => navigate('/settings')}>
                      Go to Settings
                    </Button>
                  </CardContent>
                </Card>
              );
            }

            return (
              <Alert variant="destructive">
                <AlertDescription>
                  {message || 'Failed to load deals. Please try again.'}
                </AlertDescription>
              </Alert>
            );
          })()}

          {/* Table-view sync failures. The card list surfaces its own per-row
              error; without this the table's sync could fail silently. */}
          {syncMutation.isError && (
            <SyncFailureAlert
              errorMessage={apiErrorMessage(
                syncMutation.error,
                'Failed to sync deal. Please try again.',
              )}
              className="mb-4"
            />
          )}

          {!isLoading && !error && filteredDeals.length === 0 && (
            <div className="flex flex-col items-center gap-3 py-12 text-center text-muted-foreground">
              {searchQuery || columnFiltersActive
                ? 'No deals match your filters.'
                : 'No closed-won deals found in HubSpot.'}
              {columnFiltersActive && (
                <Button size="sm" variant="outline" onClick={clearColumnFilters}>
                  Clear column filters
                </Button>
              )}
            </div>
          )}

          {!isLoading && !error && filteredDeals.length > 0 && (
            viewMode === 'table' ? (
              <DealsTable
                deals={filteredDeals}
                onRowClick={openDealInNewTab}
                onLink={(deal) => setLinkTarget(deal)}
                onSync={handleTableSync}
                syncingDealId={syncingDealId}
                sort={sort}
                onToggleSort={handleToggleSort}
                filters={columnFilters}
                filterOptions={filterOptions}
                onFilterChange={handleFilterChange}
              />
            ) : (
              <div>
                {filteredDeals.map((deal) => (
                  <DealListItem
                    key={deal.hubspot_deal_id}
                    deal={deal}
                    onClick={() => openDealInNewTab(deal)}
                    hubspotPortalId={tenantSettings?.hubspot_portal_id}
                    stripeAccountId={tenantSettings?.stripe_account_id}
                    stripeEnvironment={tenantSettings?.stripe_environment}
                  />
                ))}
              </div>
            )
          )}
        </div>
      </div>

      <SyncConfirmationDialog
        open={Boolean(syncTarget)}
        dealId={syncTarget?.hubspot_deal_id ?? ''}
        onOpenChange={(open) => {
          if (!open) setSyncTarget(null);
        }}
        onConfirm={confirmTableSync}
        isSyncing={syncMutation.isPending}
      />

      {linkTarget && (
        <LinkExistingDialog
          deal={linkTarget}
          open={true}
          onOpenChange={(o) => {
            if (!o) setLinkTarget(null);
          }}
        />
      )}
    </div>
  );
};

export default DealList;
